import React, { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import twemoji from "twemoji";
import { Translator } from "./translator";
import PeopleMap from "./page";

const BASE = "https://xmrhe4snn1.execute-api.eu-north-1.amazonaws.com";
const TWEMOJI_OPTS = { base: "https://cdnjs.cloudflare.com/ajax/libs/twemoji/14.0.2/", folder: "svg", ext: ".svg" };
const ANON_AVATAR = "/images/avatar/AnonymousAvatar.webp";


// --- formatters -------------------------------------------------------------
function formatCompact(n) {
  if (n == null) return "0";
  const num = Number(n) || 0;
  if (num < 1000) return String(num);
  if (num < 1_000_000) return (num / 1000).toFixed(num % 1000 === 0 ? 0 : 1) + "K";
  if (num < 1_000_000_000) return (num / 1_000_000).toFixed(num % 1_000_000 === 0 ? 0 : 1) + "M";
  return (num / 1_000_000_000).toFixed(1) + "B";
}

// Relative/absolute time rules:
// <1h -> Xm; <24h -> Xh; yesterday -> 'Yesterday at HH:MM'; otherwise 'Month D at HH:MM'
function formatNiceTime(ms, lang = "en") {
  if (!ms || Number(ms) <= 0) return "";                 // hide bad/missing
  let ts = Number(ms);
  if (ts < 10_000_000_000) ts *= 1000;                   // seconds → ms
  const d = new Date(ts);
  if (isNaN(d.getTime())) return "";

  const now = new Date();
  const diffSec = Math.floor((now - d) / 1000);
  if (diffSec < 60 * 60) {
    const m = Math.max(1, Math.floor(diffSec / 60));
    return `${m}m`;
  }
  if (diffSec < 60 * 60 * 24) {
    const h = Math.floor(diffSec / 3600);
    return `${h}h`;
  }
  const y = new Date(now);
  y.setDate(now.getDate() - 1);
  const isYesterday =
    d.getFullYear() === y.getFullYear() &&
    d.getMonth() === y.getMonth() &&
    d.getDate() === y.getDate();

  const timePart = d.toLocaleString(lang || "en", { hour: "numeric", minute: "2-digit" });
  if (isYesterday) return `Yesterday at ${timePart}`;

  const datePart = d.toLocaleString(lang || "en", { month: "long", day: "numeric" });
  return `${datePart} at ${timePart}`;
}




function displayNameOf(u) {
  const name =
    (u?.firstName ?? u?.given_name ?? u?.name ?? u?.uName ?? u?.displayName ?? "").trim();
  return name || u?.email || "Teche User";
}


function likeText(postLiked, postLikes) {
  const count = Number(postLikes) || 0;
  if (!postLiked) return String(count);
  const others = Math.max(0, count - 1);
  if (others === 0) return "You";
  return `You and ${others} other${others === 1 ? "" : "s"}`;
}


function avatarUrlOf(user) {
  return user?.image || user?.photoURL || user?.picture || "/avatar-placeholder.png";
}
const FEELINGS = [
  "😊 Feeling happy",
  "😢 Feeling sad",
  "😡 Feeling angry",
  "😮 Feeling surprised",
  "😌 Feeling relaxed",
  "🤒 Feeling sick",
];

function flagFromISO(code) {
  const cc = (code || "").toUpperCase();
  if (cc === "UK") return flagFromISO("GB");
  return String.fromCodePoint(...[...cc].map(ch => 0x1F1E6 + (ch.charCodeAt(0) - 65)));
}

const SPECIAL_FLAGS = [
  "\uD83C\uDFC1",                         // 🏁 Chequered
  "\uD83D\uDEA9",                         // 🚩 Triangular
  "\uD83C\uDF8C",                         // 🎌 Crossed
  "\uD83C\uDFF4",                         // 🏴 Black
  "\uD83C\uDFF3\uFE0F",                   // 🏳️ White
  "\uD83C\uDFF3\uFE0F\u200D\uD83C\uDF08", // 🏳️‍🌈 Rainbow
  "\uD83C\uDFF3\uFE0F\u200D\u26A7\uFE0F", // 🏳️‍⚧️ Transgender
  "\uD83C\uDFF4\u200D\u2620\uFE0F",       // 🏴‍☠️ Pirate
  // Subnational: England / Scotland / Wales
  "\uD83C\uDFF4\uDB40\uDC67\uDB40\uDC62\uDB40\uDC65\uDB40\uDC6E\uDB40\uDC67\uDB40\uDC7F", // England
  "\uD83C\uDFF4\uDB40\uDC67\uDB40\uDC62\uDB40\uDC73\uDB40\uDC63\uDB40\uDC74\uDB40\uDC7F", // Scotland
  "\uD83C\uDFF4\uDB40\uDC67\uDB40\uDC62\uDB40\uDC77\uDB40\uDC6C\uDB40\uDC73\uDB40\uDC7F"  // Wales
];

const ISO_FLAG_CODES = ["EU","UN","AC","AD","AE","AF","AG","AI","AL","AM","AO","AQ","AR","AS","AT","AU","AW","AX","AZ","BA","BB","BD","BE","BF","BG","BH","BI","BJ","BL","BM","BN","BO","BQ","BR","BS","BT","BV","BW","BY","BZ","CA","CC","CD","CF","CG","CH","CI","CK","CL","CM","CN","CO","CP","CR","CU","CV","CW","CX","CY","CZ","DE","DG","DJ","DK","DM","DO","DZ","EA","EC","EE","EG","EH","ER","ES","ET","FI","FJ","FK","FM","FO","FR","GA","GB","GD","GE","GF","GG","GH","GI","GL","GM","GN","GP","GQ","GR","GS","GT","GU","GW","GY","HK","HM","HN","HR","HT","HU","IC","ID","IE","IM","IN","IO","IQ","IR","IS","IT","JE","JM","JO","JP","KE","KG","KH","KI","KM","KN","KP","KR","KW","KY","KZ","LA","LB","LC","LI","LK","LR","LS","LT","LU","LV","LY","MA","MC","MD","ME","MF","MG","MH","MK","ML","MM","MN","MO","MP","MQ","MR","MS","MT","MU","MV","MW","MX","MY","MZ","NA","NC","NE","NF","NG","NI","NL","NO","NP","NR","NU","NZ","OM","PA","PE","PF","PG","PH","PK","PL","PM","PN","PR","PS","PT","PW","PY","QA","RE","RO","RS","RU","RW","SA","SB","SC","SD","SE","SG","SH","SI","SJ","SK","SL","SM","SN","SO","SR","SS","ST","SV","SX","SY","SZ","TA","TC","TD","TF","TG","TH","TJ","TK","TL","TM","TN","TO","TR","TT","TV","TW","TZ","UA","UG","US","UY","UZ","VA","VC","VE","VG","VI","VN","VU","WF","WS","XK","YE","YT","ZA","ZM","ZW"];

const ALL_FLAGS = [
  ...SPECIAL_FLAGS,
  ...ISO_FLAG_CODES.map(flagFromISO)
];


const EMOJI_SETS = {
  smileys: ["😀","😁","😂","🤣","😃","😄","😅","😆","😉","😊","😋","😎","😍","😘","😗","🥰","😙","🥲","😚","🙂","🤗","🤩","🤔","🤨","😐","😑","😶","🙄","😏","😣","😥","😮","🤐","😪","😫","🥱","😴","😌","😛","😜","🤪","😝","🤗","🤤","😒","😓","😔","🥵","🥶","😮‍💨","😵‍💫","🤯","🤧","🥳","🥸","😎","🤓","🧐","🤠","😇","🤥","😨","😭"],
  people:  ["👍","👎","👌","✌️","🤞","🤟","🤘","👏","🙌","🫶","🤝","🙏","💪","👋","🤙","✋","🖐️","🖖","☝️","👆","👇","👉","👈","👊","🤛","🤜","🤏","🫰","🫵","💅","🤳","🧑","👨","👩","🧑‍🎓","🧑‍💻","🧑‍🍳","🧑‍🎤","🧑‍🚀","🧑‍🚒","🧑‍⚕️","🧑‍🏫","🧑‍🏭"],
  activities: ["⚽","🏀","🏈","⚾","🎾","🏐","🏉","🥏","🎱","🏓","🏸","🥅","🏒","🏑","🥍","🏏","🥊","🥋","🎳","⛳","⛸️","🎣","🤿","🥌","🛼","🛹","🛷","🎮","🎲","♟️","🎯","🎰","🧩","🪩","🎻","🎸","🎺","🥁","🎹"],
  objects:   ["💡","📎","📌","🧲","🧵","🧶","🔒","🔓","🔑","🗝️","🔨","🪓","🔧","⚙️","🧰","🪛","🧪","🧫","🧬","🔬","🔭","📡","💻","🖱️","🖨️","📱","📟","☎️","📺","📷","🎥","💿","📀","💾"],
  //flags: ["🏳️", "🏴", "🏁", "🚩", "🇺🇳", "🇪🇺", "🇮🇹", "🇺🇸", "🇬🇧", "🇫🇷", "🇩🇪", "🇪🇸", "🇵🇹", "🇳🇱", "🇧🇪", "🇨🇭", "🇦🇹", "🇸🇪", "🇳🇴", "🇩🇰", "🇫🇮", "🇨🇦", "🇲🇽", "🇧🇷", "🇦🇷", "🇨🇱", "🇨🇴", "🇯🇵", "🇰🇷", "🇨🇳", "🇭🇰", "🇹🇼", "🇮🇳", "🇦🇪", "🇸🇦", "🇮🇱", "🇹🇷", "🇦🇺", "🇳🇿", "🇿🇦"],
  flags: ALL_FLAGS
};

const TABS = [
  { key: "smileys", icon: "😊" },
  { key: "people",  icon: "👥" },
  { key: "activities", icon: "⚽" },
  { key: "objects", icon: "💡" },
  { key: "flags",      icon: "🚩" },
];



function resolveAvatarUrl(raw) {
  if (!raw) return "/avatar.png";
  if (raw.startsWith("http")) return raw;
  return `${BASE}/media/${raw.replace(/^\/+/, "")}`;
}

function userAvatarRaw(u) {
  // what you store on your user object now
  return u?.profilePic || u?.image || u?.photoURL || u?.picture || "";
}



// ---- Avatar cache + resolver (module scope, not inside a component)
const avatarCache = new Map(); // email -> resolved URL
let inflight = new Map();      // email -> Promise

async function fetchAvatarByEmail(email) {
  if (!email) return "";
  if (avatarCache.has(email)) return avatarCache.get(email);
  if (inflight.has(email)) return inflight.get(email);

  const p = (async () => {
    try {
      const r = await fetch(`${BASE}/getUserProfile?email=${encodeURIComponent(email)}`);
      const j = await r.json();
      const raw = j?.user?.profilePic || j?.user?.image || j?.user?.photoURL || j?.user?.picture || "";
      const url = resolveAvatarUrl(raw);
      avatarCache.set(email, url);
      return url;
    } catch {
      const url = resolveAvatarUrl(""); // placeholder
      avatarCache.set(email, url);
      return url;
    } finally {
      inflight.delete(email);
    }
  })();

  inflight.set(email, p);
  return p;
}


function PollModal({open, onClose, anonymous, setAnonymous, avatarUrl, user, communityTitle, postText, setPostText, pollQuestion, setPollQuestion, pollOptions, setPollOptions, onSave}) {
  const [emojiOpen, setEmojiOpen] = React.useState(false);
  const [emojiTab, setEmojiTab] = React.useState("smileys");
  const postRef = React.useRef(null);
  const [caret, setCaret] = React.useState(0);

  if (!open) return null;

  const nonEmptyOpts = pollOptions.map(o => (o || "").trim()).filter(Boolean);
  const canSave = postText.trim().length > 0 && nonEmptyOpts.length >= 2;

  const addOption = () => setPollOptions(prev => [...prev, ""]);
  const removeOption = (idx) => setPollOptions(prev => prev.filter((_, i) => i !== idx));

  // keep track of caret in textarea so we can insert at cursor
  const updateCaret = (e) => setCaret(e.target.selectionStart ?? 0);

  const insertEmoji = (emoji) => {
    setPostText((prev) => {
      const start = prev.slice(0, caret);
      const end   = prev.slice(caret);
      const next  = `${start}${emoji}${end}`;
      const newPos = caret + emoji.length;
      // update the DOM selection on next tick
      requestAnimationFrame(() => {
        if (postRef.current) {
          postRef.current.focus();
          postRef.current.selectionStart = postRef.current.selectionEnd = newPos;
          setCaret(newPos);
        }
      });
      return next;
    });
  };


  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center">
      {/* backdrop */}
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      {/* panel */}
      <div className="relative w-full max-w-lg bg-white rounded-2xl shadow-xl">
        {/* header */}
        <div className="flex items-center justify-between px-4 py-3 border-b">
          <h3 className="text-lg font-semibold">Create post</h3>
          <button onClick={onClose} className="p-1 rounded-full hover:bg-gray-100">
            <span className="sr-only">Close</span> ✕
          </button>
        </div>

        {/* body */}
        <div className="px-4 py-3 space-y-4">
          {/* Anonymous toggle */}
          <div className="flex items-center justify-between">
            <span className="font-medium">Post anonymously</span>
            <button
              onClick={() => setAnonymous(v => !v)}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition ${anonymous ? "bg-[#324B8E]" : "bg-gray-300"}`}
              role="switch" aria-checked={anonymous}
            >
              <span className={`inline-block h-5 w-5 transform rounded-full bg-white transition ${anonymous ? "translate-x-5" : "translate-x-1"}`} />
            </button>
          </div>

          {/* Author + group */}
          <div className="flex items-center gap-3">
            <img src={anonymous ? ANON_AVATAR : avatarUrl} onError={e=>{e.currentTarget.src="/avatar.png";}}
                 className="w-10 h-10 rounded-full object-cover ring-2 ring-white shadow" alt="" />
            <div>
              <div className="font-medium">{anonymous ? "Anonymous" : (user?.firstName || user?.uName || user?.email || "You")}</div>
              <div className="text-xs text-gray-500">Public group · {communityTitle}</div>
            </div>
          </div>

          {/* Post text */}
          <textarea
            ref={postRef}
            value={postText}
            onChange={(e)=> setPostText(e.target.value)}
            onClick={updateCaret}
            onKeyUp={updateCaret}
            onSelect={updateCaret}
            placeholder="Create a public post..."
            rows={3}
            className="w-full border rounded-xl p-3 outline-none focus:ring-2 focus:ring-[#324B8E]"
          />

          {/* Add poll */}
          <div className="border rounded-xl">
            <div className="flex items-center justify-between px-4 py-3 border-b">
              <div className="font-semibold">Add poll</div>
              <button onClick={onClose} className="p-1 rounded-full hover:bg-gray-100">✕</button>
            </div>

            <div className="p-4 space-y-3">
              <input
                type="text"
                value={pollQuestion}
                onChange={(e)=> setPollQuestion(e.target.value)}
                placeholder="Ask a question (optional)"
                className="w-full border rounded-lg px-3 py-2"
              />

              {pollOptions.map((opt, idx) => (
                <div key={idx} className="flex items-center gap-2">
                  <input
                    type="text"
                    value={opt}
                    onChange={(e)=> {
                      const copy = [...pollOptions];
                      copy[idx] = e.target.value;
                      setPollOptions(copy);
                    }}
                    placeholder={`Option ${idx+1}`}
                    className="flex-1 border rounded-lg px-3 py-2"
                  />
                  <button
                    type="button"
                    onClick={() => removeOption(idx)}
                    className="h-10 w-10 flex items-center justify-center rounded-full border hover:bg-gray-50"
                    title="Remove"
                  >
                    ✕
                  </button>
                </div>
              ))}

              <button
                type="button"
                onClick={addOption}
                className="w-full mt-1 border rounded-lg px-3 py-2 text-sm hover:bg-gray-50"
              >
                + Add option
              </button>
            </div>
          </div>

          {/* Add to your post (icons row – decorative) */}
          <div className="relative px-1">
            <div className="flex items-center justify-between">
              <div className="text-sm text-gray-700">Add to your post</div>
              <div className="flex items-center gap-2">
                {/* Other icons */}
                <button type="button" className="text-xl px-2 py-1 rounded-lg hover:bg-gray-100">🖼️</button>
                <button type="button" className="text-xl px-2 py-1 rounded-lg hover:bg-gray-100">👥</button>
                <button type="button" className="text-xl px-2 py-1 rounded-lg hover:bg-gray-100">📍</button>

                
              </div>
            </div>
          </div>
        </div>
        
        {/* footer */}
        <div className="px-4 pb-4">
          {!postText.trim() && (
            <p className="text-xs text-red-600 mb-2">You can't create a poll without text in your post.</p>
          )}
          <button
            onClick={() => onSave({ question: (pollQuestion || "").trim(), options: nonEmptyOpts })}
            disabled={!canSave}
            className={`w-full py-2 rounded-xl ${canSave ? "bg-[#324B8E] text-white" : "bg-gray-200 text-gray-500 cursor-not-allowed"}`}
          >
            Save poll
          </button>
        </div>
      </div>
    </div>
  );
}



// Modal for showing Create Post
function CreatePostModal({open, onClose, user, communityTitle, avatarUrl, postText, setPostText, anonymous, setAnonymous, feeling, setFeeling, isPollOpen, setIsPollOpen, emojiOpenInline, setEmojiOpenInline, emojiTabInline, setEmojiTabInline, insertEmojiInline, submitPost, sendingPost, TT, postFiles, postPreviews, addPostFiles, removePostFile}) {
  const modalInputRef = React.useRef(null);

  // lock background scroll + focus textarea
  React.useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const t = setTimeout(() => modalInputRef.current?.focus(), 0);
    return () => { document.body.style.overflow = prev; clearTimeout(t); };
  }, [open]);

  // Escape key to close modal
  React.useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[300] flex items-center justify-center tw-emoji-modal">
      {/* backdrop */}
      <div className="absolute inset-0 bg-black/60" onClick={onClose} />

      {/* panel */}
      <div className="relative w-full max-w-lg bg-white rounded-2xl shadow-xl">
        {/* header */}
        <div className="flex items-center justify-between px-4 py-3 border-b">
          <h3 className="text-lg font-semibold">Create post</h3>
          <button onClick={onClose} className="h-8 w-8 rounded-full hover:bg-gray-100">✕</button>
        </div>

        {/* author row */}
        <div className="px-4 pt-3 flex items-center gap-3">
          <img src={anonymous ? ANON_AVATAR : avatarUrl} onError={e=>{e.currentTarget.src="/avatar.png";}}
               alt="" className="w-10 h-10 rounded-full object-cover ring-2 ring-white shadow" />
          <div className="min-w-0">
            <div className="font-medium truncate">{anonymous ? "Anonymous" : (user?.firstName || user?.uName || user?.email || "You")}</div>
            <div className="text-xs text-gray-500">Public group · {communityTitle}</div>
          </div>
        </div>

        {/* toggle anonymous */}
        <div className="px-4 mt-3">
          <button
            onClick={() => setAnonymous(v => !v)}
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition ${anonymous ? "bg-[#324B8E]" : "bg-gray-300"}`}
            role="switch" aria-checked={anonymous}
            title="Post anonymously"
          >
            <span className={`inline-block h-5 w-5 transform rounded-full bg-white transition ${anonymous ? "translate-x-5" : "translate-x-1"}`} />
          </button>
          <span className="ml-2 text-sm">Post anonymously</span>
        </div>

        {/* body */}
        <div className="px-4 py-3">
          {feeling && (
            <div className="mb-2 inline-flex items-center gap-2 text-sm bg-yellow-50 border border-yellow-200 text-yellow-800 px-3 py-1.5 rounded-full">
              {feeling}
              <button type="button" onClick={()=> setFeeling("")} className="ml-1">✕</button>
            </div>
          )}

          <textarea
            ref={modalInputRef}
            value={postText}
            onChange={(e)=> setPostText(e.target.value)}
            rows={5}
            placeholder={TT.createPostPlaceholder}
            className="w-full border rounded-xl p-3 outline-none focus:ring-2 focus:ring-[#324B8E]"
          />

            {/* Photo previews */}
            {postPreviews?.length > 0 && (
              <div className="mt-3 grid grid-cols-3 sm:grid-cols-4 gap-2">
                {postPreviews.map((url, i) => (
                  <div key={i} className="relative group">
                    <img src={url} alt="" className="w-full h-28 object-cover rounded-lg border" />
                    <button
                      type="button"
                      onClick={() => removePostFile(i)}
                      className="absolute top-1 right-1 w-7 h-7 rounded-full bg-black/60 text-white text-sm hidden group-hover:flex items-center justify-center"
                      title="Remove"
                    >
                      ✕
                    </button>
                  </div>
                ))}
              </div>
            )}


            {/* Drag & drop area */}
            <div
              onDragOver={(e)=> e.preventDefault()}
              onDrop={(e)=> { e.preventDefault(); addPostFiles(e.dataTransfer.files); }}
              className="mt-3 rounded-xl border-2 border-dashed p-3 text-sm text-gray-500"
            >
              Drag & drop images here
            </div>

          {/* Add to post */}
          <div className="mt-3">
            <div className="text-sm text-gray-700 mb-2">Add to your post</div>
            <div className="flex items-center gap-2">
              {/* Photos */}
              <label className="text-xl px-2 py-1 rounded-lg hover:bg-gray-100 cursor-pointer" title="Add photos">
                🖼️
                <input
                  type="file"
                  accept="image/*"
                  multiple
                  className="hidden"
                  onChange={(e) => {
                    if (e.target.files?.length) addPostFiles(e.target.files);
                    // reset input so picking the same file again fires change
                    e.target.value = "";
                  }}
                />
              </label>

              {/* Poll */}
              <button type="button" onClick={()=> setIsPollOpen(true)}
                      className="text-xl px-2 py-1 rounded-lg hover:bg-gray-100" title="Poll">📊</button>

              {/* Feeling */}
              <button type="button" onClick={()=> setFeeling("😊 Feeling happy")}
                      className="text-xl px-2 py-1 rounded-lg hover:bg-gray-100" title="Feeling">😊</button>

              {/* Emoji */}
              <div className="relative">
                <button
                  type="button"
                  onClick={() => setEmojiOpenInline(v => !v)}
                  className="text-xl px-2 py-1 rounded-lg hover:bg-gray-100"
                  title="Emoji"
                >
                  🙂
                </button>

                {emojiOpenInline && (
                  <>
                    {/* MOBILE: centered dialog + backdrop */}
                    <button
                      className="fixed inset-0 z-[399] md:hidden bg-black/30"
                      onClick={() => setEmojiOpenInline(false)}
                      aria-label="Close emoji picker"
                    />
                    <div
                      className="md:hidden fixed inset-0 z-[400] flex items-center justify-center p-4"
                      role="dialog"
                      aria-label="Emoji picker"
                    >
                      <div className="w-[min(92vw,22rem)] max-h-[70vh] overflow-auto bg-white border rounded-2xl shadow p-2">
                        <div className="flex items-center gap-1 mb-2 sticky top-0 bg-white pt-1">
                          {TABS.map(t => (
                            <button
                              key={t.key}
                              onClick={() => setEmojiTabInline(t.key)}
                              className={`h-8 w-8 rounded-full text-lg ${emojiTabInline===t.key ? "bg-gray-200" : "hover:bg-gray-100"}`}
                              title={t.key}
                            >
                              {t.icon}
                            </button>
                          ))}
                          <button onClick={() => setEmojiOpenInline(false)} className="ml-auto h-8 w-8 rounded-full hover:bg-gray-100">✕</button>
                        </div>
                        <div className="emoji-grid grid grid-cols-8 gap-2 max-h-[60vh] overflow-auto">
                          {(EMOJI_SETS[emojiTabInline] || []).map((e,i)=>(
                            <button
                              key={i}
                              className="h-9 w-9 text-xl rounded-lg hover:bg-gray-100"
                              onClick={() => insertEmojiInline(e)}
                              dangerouslySetInnerHTML={{ __html: twemoji.parse(e, TWEMOJI_OPTS) }}
                              aria-label={e}
                              title={e}
                            />
                          ))}
                        </div>
                      </div>
                    </div>

                    {/* DESKTOP/TABLET: anchored to the button */}
                    <div
                      className="hidden md:block absolute right-0 mt-2 z-[400] w-72 bg-white border rounded-2xl shadow p-2"
                      role="dialog"
                      aria-label="Emoji picker"
                    >
                      <div className="flex items-center gap-1 mb-2">
                        {TABS.map(t => (
                          <button
                            key={t.key}
                            onClick={() => setEmojiTabInline(t.key)}
                            className={`h-8 w-8 rounded-full text-lg ${emojiTabInline===t.key ? "bg-gray-200" : "hover:bg-gray-100"}`}
                            title={t.key}
                          >
                            {t.icon}
                          </button>
                        ))}
                        <button onClick={() => setEmojiOpenInline(false)} className="ml-auto h-8 w-8 rounded-full hover:bg-gray-100">✕</button>
                      </div>
                      <div className="emoji-grid grid grid-cols-8 gap-2 max-h-56 overflow-auto">
                        {(EMOJI_SETS[emojiTabInline] || []).map((e,i)=>(
                          <button
                            key={i}
                            className="h-9 w-9 text-xl rounded-lg hover:bg-gray-100"
                            onClick={() => insertEmojiInline(e)}
                            dangerouslySetInnerHTML={{ __html: twemoji.parse(e, TWEMOJI_OPTS) }}
                            aria-label={e}
                            title={e}
                          />
                        ))}
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* footer */}
        <div className="px-4 pb-4">
          <div className="flex justify-end gap-2">
            <button type="button" onClick={onClose} className="px-4 py-2 rounded-xl border">Cancel</button>
            <button
              onClick={async () => { await submitPost(); onClose(); }}
              disabled={sendingPost || !postText.trim()}
              className="px-5 py-2 rounded-xl bg-[#324B8E] text-white disabled:opacity-60"
            >
              {sendingPost ? "..." : TT.createPostButton}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}



// Modal for showing likers
function LikersModal({ open, onClose, title, fetchUrl, resolveAvatarUrl }) {
  const [loading, setLoading] = React.useState(false);
  const [people, setPeople] = React.useState([]);
  const [offset, setOffset] = React.useState(0);
  const [hasMore, setHasMore] = React.useState(false);

  React.useEffect(() => {
    if (!open) return;
    let abort = false;
    const load = async (reset=false) => {
      setLoading(true);
      try {
        const url = new URL(fetchUrl);
        url.searchParams.set("offset", reset ? 0 : offset);
        url.searchParams.set("limit", 50);
        const r = await fetch(url.toString());
        const j = await r.json();
        if (!abort) {
          const rows = Array.isArray(j?.likers) ? j.likers : [];
          setPeople(reset ? rows : p => [...p, ...rows]);
          setOffset(Number(j?.next_offset ?? 0));
          setHasMore(!!j?.has_more);
        }
      } catch (_) {}
      finally { if (!abort) setLoading(false); }
    };
    load(true);
    return () => { abort = true; };
  }, [open, fetchUrl]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[200]" role="dialog" aria-modal="true">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
        <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[92vw] sm:w-[420px] md:w-[480px] h-[50vh] max-h-[520px] bg-white rounded-2xl shadow-xl flex flex-col">
          <div className="px-4 py-3 border-b flex items-center justify-between">
            <div className="font-semibold">{title}</div>
              <button onClick={onClose} className="h-8 w-8 rounded-full hover:bg-gray-100">✕</button>
          </div>

        <div className="flex-1 overflow-auto">
          {people.map((p, i) => (
            <div key={p.email || i} className="px-4 py-3 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <img
                  src={resolveAvatarUrl(p.image || "")}
                  onError={e => { e.currentTarget.src="/avatar.png"; }}
                  alt=""
                  className="h-9 w-9 rounded-full object-cover bg-gray-200"
                />
                <div>
                  <div className="font-medium text-sm">{p.name || "Teche User"}</div>
                </div>
              </div>
              {/* Follow placeholder button (decorative) 
              <button className="px-3 py-1.5 rounded-lg bg-gray-100 text-xs hover:bg-gray-200">Follow</button>*/}
            </div>
          ))}
          {loading && <div className="p-4 text-sm text-gray-500">Loading…</div>}
          {!loading && people.length === 0 && <div className="p-4 text-sm text-gray-500">No likes yet.</div>}
        </div>

        {hasMore && (
          <div className="p-3 border-t">
            <button
              className="w-full rounded-xl border px-3 py-2 hover:bg-gray-50"
              onClick={async () => {
                try {
                  const url = new URL(fetchUrl);
                  url.searchParams.set("offset", offset);
                  url.searchParams.set("limit", 50);
                  const r = await fetch(url.toString());
                  const j = await r.json();
                  const rows = Array.isArray(j?.likers) ? j.likers : [];
                  setPeople(p => [...p, ...rows]);
                  setOffset(Number(j?.next_offset ?? 0));
                  setHasMore(!!j?.has_more);
                } catch {}
              }}
            >
              Load more
            </button>
          </div>
        )}
      </div>
    </div>
  );
}







// Modal for showing Comments
function CommentsModal({open, onClose, post, user, TT, lang, postLiked, postLikes, commentCount, onToggleLike, onShare, ownerAvatar,}) {

  const [loading, setLoading] = React.useState(false);
  const [comments, setComments] = React.useState([]);
  const [newText, setNewText] = React.useState("");
  const [showLikers, setShowLikers] = React.useState(false);
  const inputRef = React.useRef(null);


  // Helper: robust author display
  const authorDisplay =
    post.anonymous
      ? "Anonymous participant"
      : (post.authorName || post.authorEmail || user?.uName || user?.firstName || user?.name || "User");

  // Title for the modal header ("X's Post")
  const headerTitle = `${authorDisplay}'s post`;


  // load comments whenever opened
  React.useEffect(() => {
    if (!open) return;
    let abort = false;

    (async () => {
      setLoading(true);
      try {
        const r = await fetch(`${BASE}/post_get_comments?postID=${encodeURIComponent(post.id)}`);
        const j = await r.json();

        const rawRows = Array.isArray(j?.comments) ? j.comments : [];
        const rows = rawRows
          .filter(c => (c?.text || "").trim())
          .sort((a, b) => {
            const aTs = Number(a?.createdAt || 0);
            const bTs = Number(b?.createdAt || 0);
            const aMs = aTs < 10_000_000_000 ? aTs * 1000 : aTs;
            const bMs = bTs < 10_000_000_000 ? bTs * 1000 : bTs;
            return bMs - aMs; // newest first
          });

        // fetch avatars for unique emails
        const emails = [...new Set(rows.map(c => c.authorEmail).filter(Boolean))];
        const pairs = await Promise.all(
          emails.map(async (email) => [email, await fetchAvatarByEmail(email)])
        );
        const avatarMap = Object.fromEntries(pairs);

        // attach authorImage to each comment (prefer server-provided if present)
        const withAvatars = rows.map(c => ({
          ...c,
          authorImage:
            c.authorImage ||
            c.authorAvatar ||
            c.authorPhoto ||
            avatarMap[c.authorEmail] ||
            ""
        }));

        if (!abort) setComments(withAvatars); // guard before state update
      } catch (e) {
        console.error("load comments failed:", e);
      } finally {
        if (!abort) setLoading(false);
      }
    })();

    return () => { abort = true; };
  }, [open, post.id]);


  const addComment = async () => {
    if (!user?.email) { alert(TT.youMustLoginLike); return; }
    const text = newText.trim();
    if (!text) return;

    try {
      const r = await fetch(`${BASE}/post_add_comment`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: user.email, postID: post.id, text })
      });
      const j = await r.json();
      if (j?.status === "success") {
        setNewText("");
        setComments(prev => [
          { ...j.comment, authorName: user.firstName || user.uName || "Teche User", authorEmail: user.email, authorImage: resolveAvatarUrl(userAvatarRaw(user)), }, ...prev ]); }
    } catch (e) { console.error(e); }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[250]" role="dialog" aria-modal="true">
      {/* backdrop */}
      <div className="absolute inset-0 bg-black/60" onClick={onClose} />
      {/* panel */}
      <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2
                      w-[92vw] max-w-[720px] h-[80vh] bg-white rounded-2xl shadow-xl flex flex-col">
        {/* header */}
        <div className="px-4 py-3 border-b grid grid-cols-3 items-center">
          {/* (empty left cell keeps the middle perfectly centered) */}
          <span aria-hidden className="h-8 w-8" />

          {/* centered title, larger + responsive + truncation */}
          <div className="font-semibold text-base sm:text-lg md:text-xl lg:text-2xl text-center leading-tight truncate" title={headerTitle}> {headerTitle} </div>

          {/* close button aligned right */}
          <button onClick={onClose} className="h-8 w-8 justify-self-end rounded-full hover:bg-gray-100" aria-label="Close"> ✕ </button>
        </div>


        {/* post body preview */}
        <div className="px-4 py-3 border-b">
          <div className="flex items-start gap-2 text-gray-500">
            <img
              src={ownerAvatar}
              onError={(e)=>{ e.currentTarget.src="/avatar.png"; }}
              alt=""
              className="h-8 w-8 rounded-full object-cover bg-gray-200 flex-shrink-0"
            />

            <div className="flex flex-col leading-tight min-w-0">
              {/* First line: author */}
              <span
                className="font-medium text-gray-900 text-sm sm:text-base truncate"
                title={authorDisplay}
              >
                {authorDisplay}
              </span>

              {/* Second line: time + globe */}
              <div className="mt-0.5 flex items-center gap-2 text-xs text-gray-500">
                <span className="whitespace-nowrap">{formatNiceTime(post.createdAt, lang)}</span>
                <span>·</span>
                <span className="inline-flex items-center" title="Public" aria-label="Public">
                
                {/* globe icon */}
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4"
                    viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                  <path d="M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18Z"/>
                  <path d="M2.6 10h18.8M2.6 14h18.8M12 3c3 2.8 3 15.2 0 18M12 3c-3 2.8-3 15.2 0 18"/>
                </svg>
              </span>
            </div>
          </div>
        </div>
      
          {post.feeling && (
            <div className="mt-2 text-sm text-yellow-800 bg-yellow-50 border border-yellow-200 inline-block rounded-full px-3 py-1">
              {post.feeling}
            </div>
          )}
          <p className="mt-3 text-xl leading-8 text-center whitespace-pre-wrap">{post.text}</p>
        </div>



        {/* counts row (likes + comments) */}
        <div className="px-4 mt-1 flex items-center justify-between text-sm text-gray-600">
          {/* Likes count */}
          <button
            type="button"
            onClick={() => setShowLikers(true)}
            className="flex items-center gap-1 hover:underline"
            title="See who liked"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-4 w-4"
              viewBox="0 0 24 24"
              fill={postLiked ? "#e11d48" : "none"}
              stroke={postLiked ? "#e11d48" : "currentColor"}
              strokeWidth="1.8"
            >
              <path d="M21 8.25c0-2.485-2.099-4.5-4.688-4.5-1.937 0-3.597 1.145-4.312 2.786-.715-1.641-2.375-2.786-4.313-2.786C5.1 3.75 3 5.765 3 8.25c0 7.125 9 12 9 12s9-4.875 9-12z" />
            </svg>
            <span className="tabular-nums">{likeText(postLiked, postLikes)}</span>
          </button>

          {/* Comments count */}
          <div>{commentCount || 0} comments</div>
        </div>

        {/* actions row: Like | Comment | Share */}
        <div className="px-4 mt-1 border-t pt-1 grid grid-cols-3 text-sm text-gray-700">
          {/* LIKE */}
          <button
            type="button"
            onClick={onToggleLike}
            className={`flex items-center justify-center gap-2 py-1.5 rounded-lg hover:bg-gray-100 active:scale-[0.98] transition ${
              postLiked ? "text-[#324B8E]" : ""
            }`}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-5 w-5"
              viewBox="0 0 24 24"
              fill={postLiked ? "#e11d48" : "none"}
              stroke={postLiked ? "#e11d48" : "currentColor"}
              strokeWidth="1.8"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M21 8.25c0-2.485-2.099-4.5-4.688-4.5-1.937 0-3.597 1.145-4.312 2.786-.715-1.641-2.375-2.786-4.313-2.786C5.1 3.75 3 5.765 3 8.25c0 7.125 9 12 9 12s9-4.875 9-12z"
              />
            </svg>
            <span className="select-none">Like</span>
          </button>

          {/* COMMENT (focus the input box) */}
          <button
            type="button"
            onClick={() => {
              if (inputRef.current) {
                inputRef.current.focus();
                inputRef.current.scrollIntoView({ block: "nearest", behavior: "smooth" });
              }
            }}
            className="flex items-center justify-center gap-2 py-1.5 rounded-lg hover:bg-gray-100 active:scale-[0.98] transition"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 24 24" fill="currentColor">
              <path d="M8 10h8a1 1 0 110 2H8a1 1 0 110-2zm0 4h5a1 1 0 110 2H8a1 1 0 110-2zm4-12C6.48 2 2 6.03 2 11c0 1.93.76 3.68 2.02 5.07L2 22l5.28-2.28A9.77 9.77 0 0012 20c5.52 0 10-4.03 10-9s-4.48-9-10-9z" />
            </svg>
            <span className="select-none">Comment</span>
          </button>

          {/* SHARE */}
          <button
            type="button"
            onClick={onShare}
            className="flex items-center justify-center gap-2 py-1.5 rounded-lg hover:bg-gray-100 active:scale-[0.98] transition"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-5 w-5"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.8"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M15 8a3 3 0 1 0-6 0 3 3 0 0 0 6 0ZM21 14a3 3 0 1 0-6 0 3 3 0 0 0 6 0ZM9 22a3 3 0 1 0-6 0 3 3 0 0 0 6 0Z"
              />
              <path strokeLinecap="round" strokeLinejoin="round" d="M14.5 6.5 9.5 17M19 14l-9.5 3" />
            </svg>
            <span className="select-none">Share</span>
          </button>
        </div>


        {/* comments list */}
        <div className="flex-1 overflow-auto px-4 py-3 space-y-3">
          {loading && <div className="text-sm text-gray-500">Loading…</div>}
          {!loading && comments.length === 0 && (
            <div className="text-sm text-gray-500">No comments yet.</div>
          )}

          {comments.map((c, i) => (
            <div key={c.id || i} className="flex items-start gap-2">
              <img
                src={resolveAvatarUrl(c.authorImage || avatarCache.get(c.authorEmail) || "")}
                onError={(e)=>{ e.currentTarget.src="/avatar.png"; }}
                alt=""
                className="h-9 w-9 rounded-full object-cover bg-gray-200 flex-shrink-0"
              />
              <div className="flex-1">
                <div className="inline-block bg-gray-100 rounded-2xl px-3 py-2">
                  <div className="text-sm font-medium">{c.authorName || c.authorEmail || "User"}</div>
                  <div className="text-sm">{c.text}</div>
                </div>
                <div className="pl-3 mt-1 text-xs text-gray-500">{formatNiceTime(c.createdAt, lang)}</div>
              </div>
            </div>
          ))}
        </div>

        {/* sticky composer (like your TourPreview) */}
        <div className="p-3 border-t bg-white">
          <form
            onSubmit={(e)=>{ e.preventDefault(); addComment(); }}
            className="flex items-center gap-2"
          >
            <img
              src={resolveAvatarUrl(userAvatarRaw(user))}
              onError={(e)=>{ e.currentTarget.src="/avatar.png"; }}
              alt=""
              className="h-9 w-9 rounded-full object-cover bg-gray-200 flex-shrink-0"
            />
            <input
              ref={inputRef}
              value={newText}
              onChange={(e)=> setNewText(e.target.value)}
              className="flex-1 border rounded-full px-4 py-2 outline-none focus:ring-2 focus:ring-[#324B8E]"
              placeholder={
                user?.email ? `Comment as ${displayNameOf(user)}` : TT.youMustLoginLike
              }
              disabled={!user?.email}
              onKeyDown={(e)=> {
                if (e.key === "Enter" && newText.trim() && user?.email) {
                  e.preventDefault();
                  addComment();
                }
              }}
            />
            <button
              type="submit"
              disabled={!user?.email || !newText.trim()}
              className={`ml-1 w-8 h-8 rounded-full flex items-center justify-center bg-[#324B8E] transition
                ${(!user?.email || !newText.trim()) ? "opacity-50 cursor-not-allowed" : ""}`}
              title={TT.send}
              aria-label={TT.send}
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" viewBox="0 0 24 24" fill="white">
                <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z" />
              </svg>
            </button>
          </form>
        </div>
      </div>



      <LikersModal
        open={showLikers}
        onClose={() => setShowLikers(false)}
        title="People who reacted"
        fetchUrl={`${BASE}/post_likers?postID=${encodeURIComponent(post.id)}`}
        resolveAvatarUrl={resolveAvatarUrl}
      />

    </div>
  );
}







// Renders a single post with its own comments state
function PostItem({ post, user, TT, lang, community }) {
  const [openAll, setOpenAll] = React.useState(false);
  const [loadingComments, setLoadingComments] = React.useState(false);
  const [postComments, setPostComments] = React.useState([]);
  const [newCmt, setNewCmt] = React.useState("");
  const [commentCount, setCommentCount] = React.useState(post.commentCount || 0);

  // local like state for the post (UI-only unless you wire an endpoint)
  const [likeLocked, setLikeLocked] = React.useState(false);
  const [postLiked, setPostLiked] = React.useState(!!post.isLiked);
  const [postLikes, setPostLikes] = React.useState(Number(post.likes ?? post.likeCount ?? 0));

  const displayName = post.anonymous ? "Anonymous" : (post.authorName || post.authorEmail || "Utente2");

  const [postAuthorAvatar, setPostAuthorAvatar] = React.useState("");

  const [likeBusyLocal, setLikeBusyLocal] = React.useState(false);

  const [showPostLikers, setShowPostLikers] = React.useState(false);
  const [showCommentsModal, setShowCommentsModal] = React.useState(false);

  React.useEffect(() => {
    let alive = true;
    (async () => {
      if (!post.anonymous && post.authorEmail) {
        const url = await fetchAvatarByEmail(post.authorEmail);
        if (alive) setPostAuthorAvatar(url);
      } else {
        setPostAuthorAvatar(ANON_AVATAR);
      }
    })();
    return () => { alive = false; };
  }, [post.anonymous, post.authorEmail]);


  // load comments once (lazy)
  React.useEffect(() => {
    let done = false;
    const load = async () => {
      if (loadingComments || done) return;
      setLoadingComments(true);
      try {
        const r = await fetch(`${BASE}/post_get_comments?postID=${encodeURIComponent(post.id)}`);
        const j = await r.json();
        const raw = Array.isArray(j?.comments) ? j.comments : [];
        const comments = raw
          .filter(c => (c?.text || "").trim().length > 0)   // drop empties
          .map(c => ({ ...c, authorImage: avatarCache.get(c.authorEmail) || "" }));

        setPostComments(comments);
      } catch (e) { console.error(e); }
      finally { setLoadingComments(false); }
    };
    load();
    return () => { done = true; };
  }, [post.id]);
  

  const addComment = async (e) => {
    e.preventDefault();
    if (!user?.email) { alert(TT.youMustLoginLike); return; }
    const text = newCmt.trim();
    if (!text) return;
    try {
      const r = await fetch(`${BASE}/post_add_comment`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: user.email, postID: post.id, text })
      });
      const j = await r.json();
      if (j?.status === "success") {
        setNewCmt("");
        setPostComments(prev => [
          {
            ...j.comment,
            authorName: user.firstName || user.uName || "Teche User",
            authorEmail: user.email,
            authorImage: resolveAvatarUrl(userAvatarRaw(user)),
          },
          ...prev
        ]);
        setCommentCount(c => (c || 0) + 1);
      }
    } catch (e) { console.error(e); }
  };

  // FB-style: show last 2 by default
  const PREVIEW = 2;
  const visibleComments = (openAll ? postComments : postComments.slice(-PREVIEW))
    .filter(c => (c?.text || "").trim().length > 0);
  const hiddenCount = Math.max(0, (postComments?.length || 0) - visibleComments.length);



    useEffect(() => {
    setPostLiked(!!post.isLiked);
    setPostLikes(Number(post.likes ?? post.likeCount ?? 0));
  }, [post.id]);



  // Like/Comment/Share actions (Share copies a deep link)
  const togglePostLike = async () => {
    if (!user?.email) { alert(TT.youMustLoginLike); return; }
    if (likeLocked) return;
    setLikeLocked(true);

    const prevLiked = postLiked;
    const prevLikes = postLikes;

    const desiredLiked = !prevLiked;
    setPostLiked(desiredLiked);
    setPostLikes(prevLiked ? Math.max(0, prevLikes - 1) : prevLikes + 1);

    const url = `${BASE}/post_toggle_like`;
    const body = { postID: post.id, email: user.email };

    const tryOnce = async () => {
      const r = await fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });

      const raw = await r.text();
      if (!r.ok) {
        // try to parse JSON error, fall back to raw text
        try {
          const errJson = JSON.parse(raw);
          throw new Error(`HTTP ${r.status}: ${errJson.message || raw}`);
        } catch {
          throw new Error(`HTTP ${r.status}: ${raw}`);
        }
      }

      const j = raw ? JSON.parse(raw) : {};
      if (j?.status !== "success") throw new Error(j?.message || "like failed");
      return j;
    };

    try {
      let attempt = 0;
      for (;;) {
        try {
          const j = await tryOnce();
          console.log('toggle result', j);
          setPostLiked(!!j.isLiked);
          setPostLikes(Number(j.likes || 0));
          break;
        } catch (err) {
          attempt += 1;
          if (attempt >= 3) throw err;
          await new Promise(res => setTimeout(res, 200 * Math.pow(2, attempt)));
        }
      }
    } catch (e) {
      // rollback optimistic state
      setPostLiked(prevLiked);
      setPostLikes(prevLikes);
      console.error("Like request failed:", e);
      alert(`Like failed: ${e.message}`);
    } finally {
      setLikeLocked(false);
    }
  };








  const sharePost = async () => {
    try {
      const url = `${window.location.origin}/community/${encodeURIComponent(community?.id || "")}#post-${post.id}`;
      await navigator.clipboard.writeText(url);
      alert("Post link copied!");
    } catch {
      alert("Copy failed. You can share this page URL.");
    }
  };

  return (
    <div id={`post-${post.id}`} className="bg-white border rounded-2xl shadow-sm p-4">
      {/* header */}
      <div className="flex items-center gap-3">
  <img
    src={post.anonymous
        ? ANON_AVATAR
        : resolveAvatarUrl(post.authorImage || postAuthorAvatar || "")}
    onError={(e)=>{ e.currentTarget.src="/avatar.png"; }}
    alt=""
    className="h-10 w-10 rounded-full object-cover flex-shrink-0 bg-gray-200"
  />
  <div className="flex-1">
    <div className="font-medium">{displayName}</div>
    <div className="text-xs text-gray-500 flex items-center gap-1">
      <span>{formatNiceTime(post.createdAt, lang)}</span>
      <span>·</span>
      {/* globe icon */}
      <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5"
          viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18Z"/>
        <path d="M2.6 10h18.8M2.6 14h18.8M12 3c3 2.8 3 15.2 0 18M12 3c-3 2.8-3 15.2 0 18"/>
      </svg>
    </div>
  </div>
</div>

      {/* feeling chip */}
      {post.feeling && (
        <div className="mt-1 text-sm text-yellow-800 bg-yellow-50 border border-yellow-200 inline-block rounded-full px-3 py-1">
          {post.feeling}
        </div>
      )}

      {/* text */}
      <p className="mt-2 text-xl font-semibold leading-8 whitespace-pre-wrap text-center">
        {post.text}
      </p>

      {/* images */}
      {Array.isArray(post.images) && post.images.length > 0 && (
        <div className="mt-3 grid grid-cols-2 sm:grid-cols-3 gap-2">
          {post.images.map((p, i) => {
            const src = String(p || "");
            const url = src.startsWith("http")
              ? src
              : `${BASE}/media/${src.replace(/^\/+/, "")}`;
            return (
              <img
                key={i}
                src={url}
                alt=""
                className="w-full h-36 object-cover rounded-lg border bg-gray-100"
                onError={(e)=>{ e.currentTarget.style.display="none"; }}
              />
            );
          })}
        </div>
      )}

      {/* poll */}
      {post.poll && (Array.isArray(post.poll.options) || post.poll.question) && (
        <div className="mt-3 border rounded-xl p-3 bg-gray-50">
          {post.poll.question && <div className="font-medium mb-2">{post.poll.question}</div>}
          <div className="space-y-2">
            {(post.poll.options || []).map((opt, i) => (
              <div key={i} className="flex items-center gap-2">
                <input type="radio" disabled className="h-4 w-4" />
                <span>{opt}</span>
              </div>
            ))}
          </div>
          <div className="text-xs text-gray-500 mt-2">Voting not enabled yet</div>
        </div>
      )}

      {/* meta counts row */}
      <div className="mt-2 flex items-center justify-between text-sm text-gray-600">
        {/* reactions (heart) */}
        <button
          type="button"
          onClick={() => setShowPostLikers(true)}
          className="flex items-center gap-1 hover:underline"
          title="See who liked"
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4"
            viewBox="0 0 24 24"
            fill={postLiked ? "#e11d48" : "none"}
            stroke={postLiked ? "#e11d48" : "currentColor"}
            strokeWidth="1.8">
            <path d="M21 8.25c0-2.485-2.099-4.5-4.688-4.5-1.937 0-3.597 1.145-4.312 2.786-.715-1.641-2.375-2.786-4.313-2.786C5.1 3.75 3 5.765 3 8.25c0 7.125 9 12 9 12s9-4.875 9-12z" />
          </svg>
          <span className="tabular-nums">{likeText(postLiked, postLikes)}</span>
        </button>

        {/* comments link */}
        <button type="button" onClick={() => setShowCommentsModal(true)} className="hover:underline">
          {commentCount || 0} comments
        </button>
      </div>


      {/* actions row: Like | Comment | Share */}
      <div className="mt-1 border-t pt-1 grid grid-cols-3 text-sm text-gray-700">
      {/* LIKE */}
      <button
        type="button"
        disabled={likeLocked}
        onClick={togglePostLike}
        className={`flex items-center justify-center gap-2 py-1.5 rounded-lg hover:bg-gray-100 active:scale-[0.98] transition ${
          postLiked ? "text-[#324B8E]" : ""
        }`}
      >
        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5"
            viewBox="0 0 24 24"
            fill={postLiked ? "#e11d48" : "none"}
            stroke={postLiked ? "#e11d48" : "currentColor"} strokeWidth="1.8">
          <path strokeLinecap="round" strokeLinejoin="round"
                d="M21 8.25c0-2.485-2.099-4.5-4.688-4.5-1.937 0-3.597 1.145-4.312 2.786-.715-1.641-2.375-2.786-4.313-2.786C5.1 3.75 3 5.765 3 8.25c0 7.125 9 12 9 12s9-4.875 9-12z" />
        </svg>
        <span className="select-none">Like</span>
      </button>

      {/* COMMENT */}
      <button
        type="button"
        onClick={() => setShowCommentsModal(true)}
        className="flex items-center justify-center gap-2 py-1.5 rounded-lg hover:bg-gray-100 active:scale-[0.98] transition"
      >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 24 24" fill="currentColor">
            <path d="M8 10h8a1 1 0 110 2H8a1 1 0 110-2zm0 4h5a1 1 0 110 2H8a1 1 0 110-2zm4-12C6.48 2 2 6.03 2 11c0 1.93.76 3.68 2.02 5.07L2 22l5.28-2.28A9.77 9.77 0 0012 20c5.52 0 10-4.03 10-9s-4.48-9-10-9z" />
          </svg>
        <span className="select-none">Comment</span>
      </button>

      {/* SHARE */}
      <button
        type="button"
        onClick={sharePost}
        className="flex items-center justify-center gap-2 py-1.5 rounded-lg hover:bg-gray-100 active:scale-[0.98] transition"
      >
        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5"
            viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
          <path strokeLinecap="round" strokeLinejoin="round"
                d="M15 8a3 3 0 1 0-6 0 3 3 0 0 0 6 0ZM21 14a3 3 0 1 0-6 0 3 3 0 0 0 6 0ZM9 22a3 3 0 1 0-6 0 3 3 0 0 0 6 0Z"/>
          <path strokeLinecap="round" strokeLinejoin="round" d="M14.5 6.5 9.5 17M19 14l-9.5 3" />
        </svg>
        <span className="select-none">Share</span>
      </button>
    </div>


      {/* comments preview + “View more …” */}
      {!loadingComments && postComments.length > 0 && (
        <div className="mt-2 space-y-2">
          {!openAll && hiddenCount > 0 && (
            <button
              className="text-sm text-[#324B8E] hover:underline"
              onClick={() => setOpenAll(true)}
            >
              View {hiddenCount} more comment{hiddenCount > 1 ? "s" : ""}
            </button>
          )}

          {visibleComments.map((c) => (
            <div key={c.id} className="flex items-start gap-2">
              <img
                src={resolveAvatarUrl(c.authorImage || c.authorAvatar || c.authorPhoto || c.authorPicture || "")}
                onError={(e)=>{ e.currentTarget.src="/avatar.png"; }}
                alt=""
                className="h-8 w-8 rounded-full object-cover bg-gray-200 flex-shrink-0"
              />
              <div className="flex-1">
                <div className="inline-block bg-gray-100 rounded-2xl px-3 py-2">
                  <div className="text-sm font-medium">{c.authorName || c.authorEmail || "Utente1"}</div>
                  <div className="text-sm">{c.text}</div>
                </div>
                <div className="pl-3 mt-1 text-xs text-gray-500">{formatNiceTime(c.createdAt, lang)}</div>
              </div>
            </div>
          ))}
        </div>
      )} 
      


      {/* always-visible comment box */}
      <form onSubmit={addComment} className="mt-2 flex items-center gap-2">
        <img
          src={resolveAvatarUrl(userAvatarRaw(user))}
          onError={(e)=>{ e.currentTarget.src="/avatar.png"; }}
          alt=""
          className="h-9 w-9 rounded-full object-cover bg-gray-200 flex-shrink-0"
        />
        <input
          value={newCmt}
          onChange={(e)=> setNewCmt(e.target.value)}
          className="flex-1 border rounded-full px-4 py-2 outline-none focus:ring-2 focus:ring-[#324B8E]"
          placeholder={
            user?.email
              ? `Comment as ${displayNameOf(user)}`
              : TT.youMustLoginLike
          }
          disabled={!user?.email}
        />
        <button
          type="submit"
          disabled={!user?.email || !newCmt.trim()}
          className={`ml-1 w-8 h-8 rounded-full flex items-center justify-center bg-[#324B8E] transition
            ${(!user?.email || !newCmt.trim()) ? "opacity-50 cursor-not-allowed" : ""}`}
          title={TT.send}
          aria-label={TT.send}
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" viewBox="0 0 24 24" fill="white">
            <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z" />
          </svg>
        </button>
      </form>




      <LikersModal
        open={showPostLikers}
        onClose={() => setShowPostLikers(false)}
        title="People who reacted"
        fetchUrl={`${BASE}/post_likers?postID=${encodeURIComponent(post.id)}`}
        resolveAvatarUrl={resolveAvatarUrl}
      />



      <CommentsModal
        open={showCommentsModal}
        onClose={() => setShowCommentsModal(false)}
        post={post}
        user={user}
        TT={TT}
        lang={lang}
        postLiked={postLiked}
        postLikes={postLikes}
        commentCount={commentCount}
        onToggleLike={togglePostLike}
        onShare={sharePost}
        ownerAvatar={
          post.anonymous
            ? ANON_AVATAR
            : resolveAvatarUrl(post.authorImage || postAuthorAvatar || "")
        }
      />



    </div>
  );
}

//88888888888

export default function SingleCommunityPage() {
  const { id } = useParams();
  const isCommunityTwo = String(id) === "2";
  const { translateText, lang } = Translator();

  const [stickToViewportTop, setStickToViewportTop] = useState(false);    // track when scrolLing past the header

  const [data, setData] = useState(null);     // {community, comments, posts}
  const [loading, setLoading] = useState(true);

  const [isPollOpen, setIsPollOpen] = useState(false);

  const [likeBusy, setLikeBusy] = useState(false);
  const [followBusy, setFollowBusy] = useState(false);

  const [commentText, setCommentText] = useState("");
  const [sendingComment, setSendingComment] = useState(false);

  const [postText, setPostText] = useState("");
  const [sendingPost, setSendingPost] = useState(false);
  const [postFiles, setPostFiles] = useState([]);
  const [postPreviews, setPostPreviews] = useState([]);

  const [search, setSearch] = useState("");

  const user = useMemo(() => {
    try { return JSON.parse(localStorage.getItem("user") || "null"); }
    catch { return null; }
  }, []);

  const isLoggedIn = !!user?.email;
  const [composerOpen, setComposerOpen] = useState(false);
  const [anonymous, setAnonymous] = useState(false);
  const [feeling, setFeeling] = useState("");   // e.g. "😊 Feeling happy"
  const [showFeelingPicker, setShowFeelingPicker] = useState(false);

  // Minimal Poll UI
  const [isPoll, setIsPoll] = useState(false);
  const [pollQuestion, setPollQuestion] = useState("");
  const [pollOptions, setPollOptions] = useState(["", ""]);

  // Emoji
  const [emojiOpenInline, setEmojiOpenInline] = useState(false);
  const [emojiTabInline, setEmojiTabInline] = useState("smileys");
  const composerInputRef = React.useRef(null);
  const [caretInline, setCaretInline] = useState(0);

  //Show likers modal
  const [showCommunityLikers, setShowCommunityLikers] = useState(false);

  const [searchCache, setSearchCache] = useState({});
  const commentsIndexedRef = React.useRef(new Set());



  // Translatable labels (fixed IT/EN/FR, Translator for others)
  const baseLabelsByLang = {
    it: {
      back: "← Torna alle Community",
      comments: "Commenti",
      likes: "Mi piace",
      followers: "Follower",
      follow: "Segui",
      following: "Seguito",
      posts: "Post",
      AnonymousPost: "Post anonimo",
      Poll: "Sondaggio",
      nothingToShow: "Ancora nessun post",
      writeComment: "Scrivi un commento",
      send: "Invia",
      youMustLoginLike: "Per mettere Mi Piace devi prima accedere.",
      youMustLoginFollow: "Per seguire una Community devi prima accedere.",
      youMustLoginPost: "Per pubblicare un post devi prima accedere.",
      createPostPlaceholder: "Scrivi qualcosa per la community...",
      createPostButton: "Pubblica",
      searchPlaceholder: "Cerca in questa pagina...",
    },
    en: {
      back: "← Back to Community page",
      comments: "Comments",
      likes: "Likes",
      followers: "Followers",
      follow: "Follow",
      following: "Following",
      posts: "Posts",
      AnonymousPost: "Anonymous post",
      Poll: "Poll",
      nothingToShow: "No one posted yet",
      writeComment: "Write a comment",
      send: "Send",
      youMustLoginLike: "You must log in before you can like.",
      youMustLoginFollow: "You must log in before you can follow a community.",
      youMustLoginPost: "You must log in before you can publish a post.",
      createPostPlaceholder: "Write something for the community...",
      createPostButton: "Publish",
      searchPlaceholder: "Search in this page...",
    },
    fr: {
      back: "← Retour au Community",
      comments: "Commentaires",
      likes: "J’aime",
      followers: "Abonnés",
      follow: "Suivre",
      following: "Abonné(e)",
      posts: "Publications",
      AnonymousPost: "Publication anonyme",
      Poll: "Sondage",
      nothingToShow: "Personne n’a encore publié",
      writeComment: "Écrire un commentaire",
      send: "Envoyer",
      youMustLoginLike: "Vous devez vous connecter avant de pouvoir aimer.",
      youMustLoginFollow: "Vous devez vous connecter avant de pouvoir suivre une communauté.",
      youMustLoginPost: "Vous devez vous connecter avant de pouvoir publier.",
      createPostPlaceholder: "Écrivez quelque chose pour la communauté...",
      createPostButton: "Publier",
      searchPlaceholder: "Rechercher sur cette page...",
    },
  };

  const [TT, setTT] = useState(baseLabelsByLang[lang] || baseLabelsByLang.it);

  useEffect(() => {
    let cancelled = false;

    // 1) For Italian / English / French: use static labels, no API calls
    if (lang === "it" || lang === "en" || lang === "fr") {
      setTT(baseLabelsByLang[lang] || baseLabelsByLang.it);
      return;
    }

    // 2) For all other languages: translate from Italian base
    (async () => {
      try {
        const source = baseLabelsByLang.it;
        const entries = Object.entries(source);

        const translatedEntries = await Promise.all(
          entries.map(async ([key, value]) => {
            if (!value) return [key, value];
            const out = await translateText(value);
            return [key, out || value]; // fallback to Italian if empty
          })
        );

        if (!cancelled) {
          setTT(Object.fromEntries(translatedEntries));
        }
      } catch (err) {
        if (!cancelled) {
          // on error, fall back to Italian labels
          setTT(baseLabelsByLang.it);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [lang, translateText]);


  // USER
  const savedUser = (() => {
    try { return JSON.parse(localStorage.getItem("user")); } catch { return null; }
  })();

  const [avatarUrl, setAvatarUrl] = React.useState(() =>
    resolveAvatarUrl(savedUser?.profilePic || savedUser?.image)
  );

  React.useEffect(() => {
    const email = savedUser?.email;
    if (!email) return;

    // If you often change pictures, you can always refresh:
    fetch(`${BASE}/getUserProfile?email=${encodeURIComponent(email)}`)
      .then(r => r.json())
      .then(data => {
        const raw = data?.user?.profilePic || data?.user?.image;
        if (raw) setAvatarUrl(resolveAvatarUrl(raw));
      })
      .catch(() => {});
  }, []);

  
  // simple normalizer so search is case/diacritics insensitive
  const norm = (s="") =>
    s.normalize?.("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();
  const buildBasicHaystack = (p) =>
    [
      p.text || "",
      p.authorName || "",
      p.authorEmail || ""
    ].join(" • ");

  // update search cache when new posts arrive    
  useEffect(() => {
    if (!data?.posts) return;
    setSearchCache(prev => {
      const next = { ...prev };
      for (const p of data.posts) {
        if (!next[p.id]) next[p.id] = buildBasicHaystack(p);
      }
      return next;
    });
    // reset comment-indexed set on new data
    commentsIndexedRef.current = new Set();
  }, [data?.posts]);


  useEffect(() => {
    const term = norm(search);
    if (!term) return;                 // don't fetch if search is empty
    if (!Array.isArray(data?.posts)) return;

    // build a list of posts we haven't enriched with comments yet
    const toFetch = data.posts.filter(p => !commentsIndexedRef.current.has(p.id));
    if (toFetch.length === 0) return;

    // fetch comments for all those posts in parallel, then fold them into searchCache
    (async () => {
      try {
        const results = await Promise.allSettled(
          toFetch.map(async (p) => {
            const r = await fetch(`${BASE}/post_get_comments?postID=${encodeURIComponent(p.id)}`);
            const j = await r.json();
            const rows = Array.isArray(j?.comments) ? j.comments : [];
            const commentsBlob = rows
              .filter(c => (c?.text || "").trim())
              .map(c => [c.text || "", c.authorName || "", c.authorEmail || ""].join(" • "))
              .join(" • ");

            return { id: p.id, blob: commentsBlob };
          })
        );

        setSearchCache(prev => {
          const next = { ...prev };
          for (const res of results) {
            if (res.status === "fulfilled" && res.value) {
              const { id, blob } = res.value;
              const base = next[id] ?? "";
              next[id] = [base, blob].filter(Boolean).join(" • ");
              commentsIndexedRef.current.add(id);
            }
          }
          return next;
        });
      } catch (_) {
        // fail silently; basic post/author search still works
      }
    })();
  }, [search, data?.posts]);


  // Post file handling
  const addPostFiles = (filesLike) => {
    const files = Array.from(filesLike || []);
    if (!files.length) return;
    setPostFiles(prev => [...prev, ...files].slice(0, 10)); // cap at 10
    const previews = files.map(f => URL.createObjectURL(f));
    setPostPreviews(prev => [...prev, ...previews].slice(0, 10));
  };

  const removePostFile = (idx) => {
    setPostFiles(prev => prev.filter((_, i) => i !== idx));
    setPostPreviews(prev => {
      // revoke object URL to avoid leaks
      URL.revokeObjectURL(prev[idx]);
      return prev.filter((_, i) => i !== idx);
    });
  };



  const fetchAll = async () => {
    setLoading(true);
    try {
      const emailQS = user?.email ? `&email=${encodeURIComponent(user.email)}` : "";
      const res = await axios.get(`${BASE}/getCommunityById?id=${encodeURIComponent(id)}${emailQS}`);
      setData(res.data);
    } catch (e) {
      console.error("getCommunityById error", e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAll(); }, [id]);

  // Translate backend fields (category, title, desc)
  useEffect(() => {
    if (!data?.community) return;

    if (lang === "it") {
      return; // keep original text
    }

    const c = data.community;

    (async () => {
      try {
        const newCategory = await translateText(c.category || "");
        const newTitle    = await translateText(c.title || "");
        const newDesc     = await translateText(c.desc || "");

        setData(prev => prev ? ({
          ...prev,
          community: {
            ...prev.community,
            category: newCategory || c.category,
            title: newTitle || c.title,
            desc: newDesc || c.desc,
          }
        }) : prev);

      } catch (err) {
        console.error("Translation failed", err);
      }
    })();
  }, [lang, translateText, data?.community?.id]);





  // To make the "Back to community" bar sits under the header at the top of the page, but snap to the very top once we scroll past the header.
  useEffect(() => {
    const rootStyles = getComputedStyle(document.documentElement);
    const raw = rootStyles.getPropertyValue("--teche-header-height") || "0";
    const headerHeight = parseInt(raw, 10) || 0;

    const onScroll = () => {
      const y = window.scrollY || window.pageYOffset || 0;
      setStickToViewportTop(y > headerHeight);
    };

    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();

    return () => window.removeEventListener("scroll", onScroll);
  }, []);






  const toggleLike = async () => {
    if (!user?.email) { alert(TT.youMustLoginLike); return; }
    if (likeBusy) return;
    setLikeBusy(true);

    // 1) optimistic update
    setData(prev => {
      if (!prev) return prev;
      const wasLiked = !!prev.community.isLiked;
      const prevLikes = Number(prev.community.likes || 0);

      return {
        ...prev,
        community: {
          ...prev.community,
          isLiked: !wasLiked,
          likes: wasLiked ? Math.max(0, prevLikes - 1) : prevLikes + 1,
        },
      };
    });

    try {
      // 2) server call (toggle)
      const res = await axios.get(`${BASE}/community_toggle_like`, {
        params: { communityID: id, email: user.email },
      });

      // 3) reconcile with server truth
      setData(prev => prev ? ({
        ...prev,
        community: {
          ...prev.community,
          isLiked: !!res.data.isLiked,
          likes: Number(res.data.likes) || 0,
        }
      }) : prev);
    } catch (e) {
      console.error("toggle like error", e);
      // 4) rollback on error
      setData(prev => {
        if (!prev) return prev;
        // rollback the optimistic change
        const wasLiked = !prev.community.isLiked; // because we flipped it earlier
        const prevLikes = Number(prev.community.likes || 0);
        return {
          ...prev,
          community: {
            ...prev.community,
            isLiked: wasLiked,
            likes: wasLiked ? prevLikes + 1 : Math.max(0, prevLikes - 1),
          },
        };
      });
      alert("Like failed. Please try again.");
    } finally {
      setLikeBusy(false);
    }
  };


  const toggleFollow = async () => {
    if (!user?.email) { alert(TT.youMustLoginFollow); return; }
    if (followBusy) return;
    setFollowBusy(true);
    try {
      const res = await axios.get(`${BASE}/community_toggle_follow`, {
        params: { communityID: id, email: user.email }
      });
      setData(prev => prev ? ({
        ...prev,
        community: {
          ...prev.community,
          isFollowing: !!res.data.isFollowing,
          followers: Number(res.data.followers) || 0
        }
      }) : prev);
    } catch (e) {
      console.error("toggle follow error", e);
    } finally {
      setFollowBusy(false);
    }
  };

  const submitComment = async (e) => {
    e.preventDefault();
    if (!user?.email) { alert(TT.youMustLoginLike); return; }
    const text = commentText.trim();
    if (!text) return;
    setSendingComment(true);
    try {
      const res = await fetch(`${BASE}/community_add_comment`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: user.email, communityID: id, text })
      });
      const j = await res.json();
      if (j?.status === "success") {
        setCommentText("");
        setData(prev => prev ? ({
          ...prev,
          community: { ...prev.community, comments: prev.community.comments + 1 },
          comments: [
            {
              ...j.comment,
              authorName: user.firstName || user.uName || "Me",
              authorEmail: user.email,
              authorImage: resolveAvatarUrl(userAvatarRaw(user)),
            },
            ...prev.comments
          ]
        }) : prev);
      }
    } catch (e) {
      console.error("add comment error", e);
    } finally {
      setSendingComment(false);
    }
  };

  const submitPost = async (e) => {
    e?.preventDefault?.();
    if (!user?.email) { alert(TT.youMustLoginPost); return; }
    const text = postText.trim();
    if (!text) return;

    setSendingPost(true);
    try {
      // 1) upload media (if any)
      let imagePaths = [];   // backend relative paths
      let imageUrls  = [];   // fully-qualified URLs (if backend returns them)
      if (postFiles.length) {
        const fd = new FormData();
        postFiles.forEach(f => fd.append("files", f, f.name));
        fd.append("email", user.email);
        fd.append("communityID", id);
        const uploadResp = await fetch(`${BASE}/community_upload_post_media`, {
          method: "POST",
          body: fd
        });
        const uploadJson = await uploadResp.json();
        console.log("UPLOAD RESPONSE to OMAR:", uploadJson);
        if (uploadJson?.status === "success") {
          imagePaths = (uploadJson.files || []).map(f => f.path);
          imageUrls  = (uploadJson.files || []).map(f => f.url);
        }
      }

      // 2) create post with image references
      const body = {
        email: user.email,
        communityID: id,
        text,
        anonymous,
        feeling,
        images: imagePaths,
        poll: isPoll && (pollQuestion.trim() || pollOptions.some(o => (o || "").trim()))
          ? { question: pollQuestion.trim(), options: pollOptions.map(o => o.trim()).filter(Boolean) }
          : null
      };

      const res = await fetch(`${BASE}/community_add_post`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body)
      });
      const j = await res.json();
      if (j?.status === "success") {
        setPostText("");
        setAnonymous(false);
        setFeeling("");
        setIsPoll(false);
        setPollQuestion("");
        setPollOptions(["", ""]);
        setPostFiles([]);
        postPreviews.forEach(u => URL.revokeObjectURL(u));
        setPostPreviews([]);
        setComposerOpen(false);

        setData(prev => prev ? ({
          ...prev,
          posts: [
            {
              ...j.post,
              authorName: (j.post.anonymous ? "Anonymous" : (user.firstName || user.uName || "Me")),
              authorEmail: user.email,
              authorImage: resolveAvatarUrl(userAvatarRaw(user)),
              images: imagePaths,
            },
            ...prev.posts
          ]
        }) : prev);
      }
    } catch (e) {
      console.error("add post error", e);
    } finally {
      setSendingPost(false);
    }
  };


  // Emoji helper function
  const insertEmojiInline = (emoji) => {
    setPostText((prev) => {
      const start = prev.slice(0, caretInline);
      const end   = prev.slice(caretInline);
      const next  = `${start}${emoji}${end}`;
      requestAnimationFrame(() => {
        if (composerInputRef.current) {
          const newPos = caretInline + emoji.length;
          composerInputRef.current.focus();
          composerInputRef.current.selectionStart = composerInputRef.current.selectionEnd = newPos;
          setCaretInline(newPos);
        }
      });
      return next;
    });
  };


  if (loading || !data) {
    return (
      <div className="max-w-5xl mx-auto px-4 py-10">
        <div className="animate-pulse h-64 rounded-2xl bg-gray-100" />
        <div className="mt-6 h-6 w-1/2 bg-gray-100 rounded" />
        <div className="mt-2 h-5 w-2/3 bg-gray-100 rounded" />
      </div>
    );
  }

  //const { community, comments, posts } = data;
  const { community, comments = [], posts = [] } = data;

  // --- Search filter (client-side across visible data) ---------------------
  const s = norm(search.trim());
  const filteredPosts = !s
    ? posts
    : posts.filter((p) => {
        const hay = norm(searchCache[p.id] || buildBasicHaystack(p));
        return hay.includes(s);
      });


  // helper: case/diacritics-insensitive "Utente" check
  const isUtente = (p) => {
    const raw = (p?.authorName || "").normalize("NFD").replace(/[\u0300-\u036f]/g, "").trim().toLowerCase();
    return raw === "utente";
  };

  // Sort newest → oldest using createdAt (handles ms or seconds)
  const sortedPosts = [...filteredPosts].sort((a, b) => {
    const aTs = Number(a?.createdAt || 0);
    const bTs = Number(b?.createdAt || 0);
    // normalize seconds → ms if needed
    const aMs = aTs < 10_000_000_000 ? aTs * 1000 : aTs;
    const bMs = bTs < 10_000_000_000 ? bTs * 1000 : bTs;
    return bMs - aMs;
  });


  //To hide the first post if its author is "Utente"
  const visiblePosts =
    sortedPosts.length && isUtente(sortedPosts[0])
      ? sortedPosts.slice(1)
      : sortedPosts;


  return (
    <>
    <style>{`
      .tw-emoji-modal .emoji-grid img.emoji {
        width: 50%;
        height: 50%;
        object-fit: contain;
        display: inline-block;
      }
      :root {
        /* approximate header height; tweak if needed */
        --teche-header-height: 64px;
      }

      @media (min-width: 768px) {
        :root {
          --teche-header-height: 72px;
        }
      }
    `}</style>



    <div className="relative">
      {/* Fixed back bar under the main site header */}
      <div
        className="fixed z-30 bg-transparent border-b border-transparent"
        style={{
          top: stickToViewportTop ? 0 : "var(--teche-header-height)",
          left: "4%",
          width: "70%",
          transition: "top 0.3s ease"
        }}
      >
        <div className="px-3 sm:px-4 md:px-6 lg:px-8">
          <Link to="/community" className="inline-flex items-center gap-2 py-2 px-4 text-sm sm:text-base md:text-xl font-medium text-[#324B8E] rounded-xl backdrop-blur-md bg-white/20 hover:bg-white/30 transition">
            {TT.back}
          </Link>
        </div>
      </div>


      <div className="container mx-auto px-3 sm:px-4 md:px-6 lg:px-8 pt-16 sm:pt-20 pb-6 sm:pb-8">
        {/* Cover */}
        <div className="mt-4 relative w-full aspect-[16/7] rounded-2xl overflow-hidden shadow">
          <img src={community.image} alt={community.title} className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent" />
          <div className="absolute bottom-4 left-4 right-4 text-white">
            <h1 className="text-2xl sm:text-3xl font-bold">{community.title}</h1>
            <p className="opacity-90 text-sm sm:text-base mt-1">{community.desc}</p>
          </div>
        </div>



        {/* render the map "Share my location"*/}
        {isCommunityTwo && isLoggedIn && <PeopleMap />}


      

        {/* Actions row under image */}
        <div className="mt-4 flex flex-col gap-3 md:gap-4 md:flex-row md:items-center md:justify-between">
          {/* Left: metrics + like/comment icons */}
          <div className="flex flex-wrap items-center gap-2 sm:gap-3">
            {/* Like button (RED when liked) */}
            <button
              onClick={() => setShowCommunityLikers(true)}
              disabled={likeBusy}
              className="px-3 py-1.5 rounded-full border border-gray-300 bg-white hover:bg-gray-50 inline-flex items-center gap-2"
              title={TT.likes}
            >
              {/* Heart like icon */}
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5"
                  viewBox="0 0 24 24"
                  fill={community.isLiked ? "#e11d48" : "none"}
                  stroke={community.isLiked ? "#e11d48" : "currentColor"}
                  strokeWidth="1.8">
                <path strokeLinecap="round" strokeLinejoin="round"
                      d="M21 8.25c0-2.485-2.099-4.5-4.688-4.5-1.937 0-3.597 1.145-4.312 2.786-.715-1.641-2.375-2.786-4.313-2.786C5.1 3.75 3 5.765 3 8.25c0 7.125 9 12 9 12s9-4.875 9-12z" />
              </svg>
              <span className="text-sm font-medium">{community.likes ?? 0}</span>
            </button>

            {/* Comments indicator (speech bubble) */}
            <div className="px-3 py-1.5 rounded-full border border-gray-300 bg-white inline-flex items-center gap-2" title={TT.comments}>
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5"
                  viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
                <path strokeLinecap="round" strokeLinejoin="round"
                      d="M7 8h10M7 12h7m-7 8l-4 3v-3H4a8 8 0 008-8V5a4 4 0 014-4h2a4 4 0 014 4v7a8 8 0 01-8 8H7z"/>
              </svg>
              <span className="text-sm font-medium">{community.comments ?? 0}</span>
            </div>

            {/* Followers */}
            <div className="px-3 py-1.5 rounded-full border border-gray-300 bg-white inline-flex items-center gap-2" title={TT.followers}>
              {/* Users icon */}
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5"
                  viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
                <path strokeLinecap="round" strokeLinejoin="round"
                      d="M17 20v-2a4 4 0 00-4-4H7a4 4 0 00-4 4v2M9 12a4 4 0 100-8 4 4 0 000 8zM23 20v-2a4 4 0 00-3-3.874"/>
                <circle cx="20" cy="8" r="3"/>
              </svg>
              <span className="text-sm font-medium">{formatCompact(community.followers)}</span>
            </div>
          </div>

          {/* Right: Follow + Search */}
          <div className="flex items-center gap-2 sm:gap-3 w-full md:w-auto">
            <button
              onClick={() => {
                if (!isLoggedIn) {
                  alert(TT.youMustLoginFollow);
                  return;
                }
                toggleFollow();
              }}
              disabled={followBusy || !isLoggedIn}
              className={`px-4 py-2 rounded-full border transition
                ${
                  !isLoggedIn
                    ? "bg-gray-200 text-gray-500 border-gray-300 cursor-not-allowed"
                    : community.isFollowing
                      ? "bg-[#324B8E] text-white border-[#324B8E]"
                      : "bg-white text-[#324B8E] border-[#324B8E] hover:bg-[#324B8E]/10"
                }`}
            >
              {community.isFollowing ? TT.following : TT.follow}
            </button>

            <div className="relative flex-1 md:flex-none">
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={TT.searchPlaceholder}
                className="w-full md:w-72 border rounded-full py-2 pl-10 pr-3 outline-none focus:ring-2 focus:ring-[#324B8E]"
              />
              {/* search icon */}
              <svg xmlns="http://www.w3.org/2000/svg" className="absolute left-3 top-2.5 h-5 w-5"
                  viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
                <path strokeLinecap="round" strokeLinejoin="round"
                      d="M21 21l-4.35-4.35M10 18a8 8 0 110-16 8 8 0 010 16z"/>
              </svg>
            </div>
          </div>
        </div>


        {/* Post Composer */}
        {isLoggedIn && (
          <div className="mt-6 bg-white border rounded-2xl shadow-sm p-3 sm:p-4">
            {/* top row: avatar + input */}
            <div className="flex items-start gap-3">
              <img
                src={avatarUrl}
                alt="User"
                className="w-10 h-10 rounded-full object-cover ring-2 ring-white shadow"
                onError={(e) => { e.currentTarget.src = "/avatar.png"; }}
              />
              <div className="flex-1">
                <div
                  className="w-full border rounded-full px-4 py-2 min-h-[44px] text-gray-700 bg-gray-50 hover:bg-white focus-within:bg-white cursor-text"
                  onClick={() => setComposerOpen(true)}
                >
                  <input
                    ref={composerInputRef}
                    type="text"
                    value={postText}
                    onChange={(e)=> setPostText(e.target.value)}
                    onFocus={()=> setComposerOpen(true)}
                    onClick={()=> setComposerOpen(true)}
                    onKeyUp={(e)=> setCaretInline(e.target.selectionStart ?? 0)}
                    onSelect={(e)=> setCaretInline(e.target.selectionStart ?? 0)}
                    placeholder={TT.createPostPlaceholder}
                    className="w-full bg-transparent outline-none"
                  />
                </div>
              </div>
            </div>

            {/* bottom actions: Anonymous | Poll | Feeling */}
            <div 
              className=" mt-4 flex flex-nowrap items-stretch gap-2 sm:gap-3 overflow-x-auto no-scrollbar snap-x snap-mandatory md:grid md:grid-cols-3 md:gap-3 md:overflow-visible md:snap-none"
              role="group"
              aria-label="Post actions"
            >

              {/* Anonymous */}
              <button
                type="button"
                onClick={()=> setAnonymous(v => !v)}
                className={`shrink-0 snap-start min-w-[160px] sm:min-w-[180px] md:min-w-0 md:shrink md:w-full
                  flex items-center justify-center gap-2 rounded-xl border px-3 py-2 text-sm
                  ${anonymous ? "bg-[#324B8E] text-white border-[#324B8E]" : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"}`}
                title={TT.AnonymousPost}
              >
                {/* mask icon */}
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5"
                    viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
                  <path strokeLinecap="round" strokeLinejoin="round"
                    d="M3 7s3-3 9-3 9 3 9 3v3a8 8 0 01-8 8h-2a8 8 0 01-8-8V7zM8 12h.01M16 12h.01"/>
                </svg>
                {TT.AnonymousPost}
              </button>

              {/* Poll */}
              <button
                type="button"
                onClick={()=> setIsPollOpen(true)}
                className={`shrink-0 snap-start min-w-[160px] sm:min-w-[180px] md:min-w-0 md:shrink md:w-full
                  flex items-center justify-center gap-2 rounded-xl border px-3 py-2 text-sm
                  ${isPoll ? "bg-[#324B8E] text-white border-[#324B8E]" : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"}`}
                title={TT.Poll}
              >
                {/* bar chart icon */}
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5"
                    viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M4 20V10M12 20V4M20 20v-8"/>
                </svg>
                {TT.Poll}
              </button>

              {/* Feeling */}
              <div className="relative shrink-0 snap-start min-w-[160px] sm:min-w-[180px] md:min-w-0 md:shrink md:w-full">
                <button
                  type="button"
                  onClick={()=> setShowFeelingPicker(v => !v)}
                  className="w-full flex items-center justify-center gap-2 rounded-xl border px-3 py-2 text-sm bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
                  title="Feeling/activity"
                >
                  {/* smile icon */}
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5"
                      viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 20a8 8 0 100-16 8 8 0 000 16z"/>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M15 11h0M9 11h0M8 15s1.5 2 4 2 4-2 4-2"/>
                  </svg>
                  Feeling/activity
                </button>

                {/* picker */}
                {showFeelingPicker && (
                  <div className="absolute z-10 mt-2 w-56 bg-white border rounded-xl shadow p-2">
                    {FEELINGS.map((f) => (
                      <button
                        key={f}
                        className={`w-full text-left px-3 py-2 rounded-lg hover:bg-gray-50 ${feeling === f ? "bg-gray-100" : ""}`}
                        onClick={() => { setFeeling(f); setShowFeelingPicker(false); setComposerOpen(true); }}
                        type="button"
                      >
                        {f}
                      </button>
                    ))}
                    {feeling && (
                      <button
                        className="mt-1 w-full text-left px-3 py-2 rounded-lg hover:bg-gray-50 text-red-600"
                        onClick={() => setFeeling("")}
                        type="button"
                      >
                        Clear feeling
                      </button>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* expanded area when composing */}
            {composerOpen && (
              <form onSubmit={submitPost} className="mt-4 space-y-3">
                {isPoll && (
                  <div className="mt-3 inline-flex items-center gap-2 text-sm bg-blue-50 border border-blue-200 text-blue-800 px-3 py-1.5 rounded-full">
                    Poll added ({pollQuestion ? "Q + " : ""}{pollOptions.filter(o=>o.trim()).length} options)
                    <button className="ml-1" onClick={()=> { setIsPoll(false); setPollQuestion(""); setPollOptions(["",""]); }}>✕</button>
                  </div>
                )}
                
                {feeling && (
                  <div className="inline-flex items-center gap-2 text-sm bg-yellow-50 border border-yellow-200 text-yellow-800 px-3 py-1.5 rounded-full">
                    {feeling}
                    <button type="button" onClick={()=> setFeeling("")} className="ml-1">✕</button>
                  </div>
                )}

              <div className="flex justify-end gap-2">
                <button
                  type="button"
                  className="px-4 py-2 rounded-xl border"
                  onClick={()=>{
                    setComposerOpen(false);
                    setAnonymous(false);
                    setFeeling("");
                    setIsPoll(false);
                    setPollQuestion("");
                    setPollOptions(["", ""]);
                  }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={sendingPost || !postText.trim()}
                  className="px-5 py-2 rounded-xl bg-[#324B8E] text-white disabled:opacity-60"
                >
                  {sendingPost ? "..." : TT.createPostButton}
                </button>
              </div>
            </form>
          )}
        </div>
      )}





      {/* Posts list (each post has its own comments) */}
      <div className="mt-6 space-y-4">
        {visiblePosts.length === 0 ? (
          <div className="text-center text-sm text-gray-500">— {TT.nothingToShow} —</div>
        ) : (
          visiblePosts.map((p) => (
            <PostItem key={p.id} post={p} user={user} TT={TT} lang={lang} community={community} />
          ))
        )}
      </div>




      <PollModal
        open={isPollOpen}
        onClose={()=> setIsPollOpen(false)}
        anonymous={anonymous}
        setAnonymous={setAnonymous}
        avatarUrl={avatarUrl}
        user={user}
        communityTitle={community?.title}

        postText={postText}
        setPostText={setPostText}

        pollQuestion={pollQuestion}
        setPollQuestion={setPollQuestion}
        pollOptions={pollOptions}
        setPollOptions={setPollOptions}

        onSave={({question, options}) => {
          setPollQuestion(question);
          setPollOptions(options.length ? options : ["", ""]);
          setIsPoll(true);
          setIsPollOpen(false);
        }}
      />




      <CreatePostModal
        open={composerOpen}
        onClose={() => {
          setComposerOpen(false);
          setEmojiOpenInline(false);
        }}
        user={user}
        communityTitle={community?.title}
        avatarUrl={avatarUrl}
        postText={postText} setPostText={setPostText}
        anonymous={anonymous} setAnonymous={setAnonymous}
        feeling={feeling} setFeeling={setFeeling}
        isPollOpen={isPollOpen} setIsPollOpen={setIsPollOpen}
        emojiOpenInline={emojiOpenInline} setEmojiOpenInline={setEmojiOpenInline}
        emojiTabInline={emojiTabInline} setEmojiTabInline={setEmojiTabInline}
        insertEmojiInline={insertEmojiInline}
        submitPost={submitPost}
        sendingPost={sendingPost}
        TT={TT}
        postFiles={postFiles}
        postPreviews={postPreviews}
        addPostFiles={addPostFiles}
        removePostFile={removePostFile}
      />











        {/* Community Likers Modal */}
        <LikersModal
          open={showCommunityLikers}
          onClose={() => setShowCommunityLikers(false)}
          title="People who reacted"
          fetchUrl={`${BASE}/community_likers?communityID=${encodeURIComponent(id)}`}
          resolveAvatarUrl={resolveAvatarUrl}
        />




        </div>
      </div>
    </>
  );
}
